// Batch-number auto-increment for multi-batch creates.
//
// The create form generates N batch numbers from a starting one by incrementing
// its trailing digits. The pattern it accepts is "optional letters, then digits"
// -- so `CCYCB26011` works, and so does `a7`, but `CB26B001` does not, because
// the digits are not at the end.
//
// That mattered because the generator simply produced nothing for an
// unparseable seed, and the form then saved nothing at all: the dialog closed
// as if it had worked and no error was shown. `canIncrement` exists so the form
// can refuse up front instead of silently doing nothing.

const SEED = /^([A-Za-z]+)?(\d+)$/;

/**
 * Split a starting batch number into its fixed prefix and its trailing counter.
 * Returns null when the number cannot be incremented.
 */
export const parseBatchSeed = (batchNumber) => {
  if (typeof batchNumber !== 'string') return null;
  const match = batchNumber.trim().match(SEED);
  if (!match) return null;
  return {
    prefix: match[1] || '',
    start: parseInt(match[2], 10),
    // Keep the original width so 007 -> 008, not 8.
    width: match[2].length,
  };
};

/** True when a multi-batch run can be generated from this starting number. */
export const canIncrement = (batchNumber) => parseBatchSeed(batchNumber) !== null;

/**
 * The batch numbers a run of `count` would produce. Empty when the seed cannot
 * be incremented -- which is exactly the case the form must refuse.
 */
export const generateBatchNumbers = (batchNumber, count) => {
  const seed = parseBatchSeed(batchNumber);
  if (!seed || !Number.isFinite(count) || count <= 0) return [];
  return Array.from({ length: count }, (_, i) =>
    `${seed.prefix}${String(seed.start + i).padStart(seed.width, '0')}`);
};

export default { parseBatchSeed, canIncrement, generateBatchNumbers };
